import { useScope } from "~/hooks/useScope";
import { useGetWorkspaceQuery, useLoadSpaceQuery } from "~/operations";
import { Metrics, parseAssetRiskRatings } from "~/lib/arrow";

interface UseLoadScopeParams {
  skip?: boolean;
}

export function useLoadScope({ skip }: UseLoadScopeParams = {}) {
  const { activeScope } = useScope();

  const {
    data: spaceData,
    error: spaceError,
    loading: spaceLoading,
    refetch: refetchSpace,
  } = useLoadSpaceQuery({
    variables: {
      spaceMrn: activeScope?.mrn || "",
      metricMrns: [Metrics.AssetsRiskRatingDistribution],
    },
    skip: activeScope?.type !== "space" || skip,
  });

  const {
    data: workspaceData,
    error: workspaceError,
    loading: workspaceLoading,
    refetch: refetchWorkspace,
  } = useGetWorkspaceQuery({
    variables: {
      mrn: activeScope?.mrn || "",
      metricMrns: [Metrics.AssetsRiskRatingDistribution],
    },
    skip: activeScope?.type !== "workspace",
  });

  const getFindingsCount = () => {
    if (activeScope?.type === "space") {
      return spaceData?.space?.priorityFindings || 0;
    }
    if (activeScope?.type === "workspace") {
      return workspaceData?.workspace?.__typename === "Workspace"
        ? workspaceData.workspace.priorityFindings
        : 0;
    }
    return 0;
  };

  const getAssetCount = () => {
    const metricsEntries =
      (workspaceData?.workspace?.__typename === "Workspace"
        ? workspaceData.workspace.metrics?.entries
        : spaceData?.space?.metrics?.entries) || [];

    const assetRiskRatings = parseAssetRiskRatings(metricsEntries);

    return assetRiskRatings.total;
  };

  const loading = spaceLoading || workspaceLoading;
  const error = spaceError || workspaceError;
  const data = spaceData || workspaceData;

  const findingsCount = getFindingsCount();
  const assetCount = getAssetCount();
  const refetch = () => {
    refetchSpace();
    refetchWorkspace();
  };

  return { loading, error, data, findingsCount, assetCount, refetch };
}
